<script lang="ts">
    // Linha de uma lista para escolher algo: o nome em cima, os detalhes embaixo em tom discreto e,
    // opcionalmente, uma etiqueta à direita. É a mesma linha para pessoa, serviço e para as opções
    // do nosso select — é o que faz as listas do app terem todas a mesma cara.
    //
    //   <Zoption title="Consulta" details={['Duração: 01:00']} badge="Serviço" on:click={...} />
    //
    // `details` aceita texto pronto; para algo mais elaborado (valores alinhados, ícone) use o slot
    // "details". O slot padrão entra no fim da linha, abaixo de tudo.
    import { createEventDispatcher } from 'svelte'
    import Fa from 'svelte-fa'
    import { faCheck } from '@fortawesome/free-solid-svg-icons'

    const dispatch = createEventDispatcher()

    let className: string = ''
    export { className as class }
    export let title: string = ''
    export let details: string[] = []
    export let badge: string = ''
    // cor da etiqueta. As cores cheias das listas antigas gritavam demais no visual novo, então
    // aqui elas viram fundo suave com o texto na cor — continua distinguindo o tipo, sem pesar.
    export let badgeTone: 'neutral' | 'green' | 'amber' | 'blue' | 'teal' = 'neutral'
    export let selected: boolean = false
    export let disabled: boolean = false
    // cabeçalho de grupo ("Saldos do cliente", "Todos os produtos") não é escolhível
    export let header: boolean = false

    $: visibleDetails = (details || []).filter(Boolean)
</script>

{#if header}
    <div class="zoption_header {className}">
        <slot name="icon" />
        <span>{title}</span>
    </div>
{:else}
    <button type="button" class="zoption {className}" class:selected class:disabled {disabled} on:click={() => dispatch('click')}>
        <span class="zoption_main">
            <span class="zoption_title">{title}</span>
            {#if badge}
                <span class="zoption_badge -{badgeTone}">{badge}</span>
            {/if}
            {#if selected}
                <span class="zoption_check"><Fa icon={faCheck} /></span>
            {/if}
        </span>
        {#if visibleDetails.length > 0 || $$slots.details}
            <span class="zoption_details">
                <slot name="details">
                    {#each visibleDetails as detail}
                        <span>{detail}</span>
                    {/each}
                </slot>
            </span>
        {/if}
        <slot />
    </button>
{/if}

<style>
    /* valores base = tema escuro (padrão do app); as variáveis vêm de css_z_field.css */
    .zoption {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 2px;
        width: 100%;
        text-align: left;
        padding: 10px 12px;
        /* a linha de uma opção acompanha a altura de um campo (--z-field-height): lista apertada
           parece lista desligada, e no toque a linha baixa erra o alvo. Com detalhes embaixo ela
           cresce a partir daqui. */
        min-height: 42px;
        border: 0px;
        border-radius: 0px;
        background: transparent;
        color: inherit;
        font: inherit;
        cursor: pointer;
    }
    /* o traço entre linhas liga dois componentes irmãos, e não dois elementos deste arquivo — por
       isso :global, senão o compilador considera a regra sem uso e a descarta */
    :global(.zoption + .zoption) {
        border-top: 1px solid var(--z-field-border);
    }
    .zoption:hover:not(.disabled) {
        background-color: var(--z-field-act-bg);
    }
    .zoption.selected {
        background-color: #379e751f;
    }
    .zoption.disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }

    .zoption_main {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
    }
    .zoption_title {
        flex: 1 1 auto;
        min-width: 0;
        font-size: 15px;
        line-height: 1.3;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .zoption_badge {
        flex: 0 0 auto;
        font-size: 11px;
        font-weight: 600;
        line-height: 1.4;
        padding: 1px 8px;
        border-radius: 999px;
        background-color: var(--z-field-act-bg);
        color: inherit;
        opacity: 0.85;
    }
    /* as cores por tom mudam com o tema (css_z_field.css); o fundo é a própria cor bem diluída */
    .zoption_badge.-green {
        background-color: color-mix(in srgb, var(--z-tone-green) 14%, transparent);
        color: var(--z-tone-green);
    }
    .zoption_badge.-amber {
        background-color: color-mix(in srgb, var(--z-tone-amber) 14%, transparent);
        color: var(--z-tone-amber);
    }
    .zoption_badge.-blue {
        background-color: color-mix(in srgb, var(--z-tone-blue) 14%, transparent);
        color: var(--z-tone-blue);
    }
    .zoption_badge.-teal {
        background-color: color-mix(in srgb, var(--z-tone-teal) 14%, transparent);
        color: var(--z-tone-teal);
    }
    .zoption_check {
        flex: 0 0 auto;
        font-size: 12px;
        color: var(--z-field-focus);
    }
    .zoption_details {
        display: flex;
        flex-wrap: wrap;
        gap: 2px 14px;
        font-size: 12.5px;
        opacity: 0.55;
        line-height: 1.3;
    }

    .zoption_header {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 10px 12px 6px 12px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        opacity: 0.5;
    }
    :global(.zoption_header + .zoption) {
        border-top: 0px;
    }

    /* No toque a lista costuma ocupar a tela inteira (ver zinput-expander) e o dedo é um alvo
       grosso: a linha fica mais alta e o nome um ponto maior. A condição é a mesma que o
       utils.isMobile() usa para decidir a folha de tela inteira, por isso ponteiro e não largura. */
    @media (pointer: coarse) {
        .zoption {
            min-height: 56px;
            padding: 12px 14px;
            gap: 3px;
        }
        .zoption_title {
            font-size: 16px;
        }
        .zoption_details {
            font-size: 13px;
        }
        .zoption_header {
            padding: 14px 14px 8px 14px;
        }
    }
</style>
